<template>
  <div
    ref="containerRef"
    class="base-combobox"
    :class="{ 'dropdown': isButtonTrigger, 'is-active': isButtonTrigger && isOpen }"
  >
    <div v-if="isInputTrigger" class="control has-icons-right">
      <input
        ref="inputRef"
        type="text"
        class="input"
        :class="[inputClass, { 'is-danger': hasError }]"
        :value="currentSearchTerm"
        :placeholder="placeholder"
        :disabled="disabled"
        role="combobox"
        aria-haspopup="listbox"
        :aria-expanded="isOpen"
        :aria-controls="listboxId"
        :aria-activedescendant="activeDescendantId"
        autocomplete="off"
        @input.stop="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="handleKeydown"
      />
      <span v-if="loading" class="icon is-right">
        <i class="fas fa-circle-notch fa-spin has-text-grey-light" aria-hidden="true"></i>
      </span>
      <span
        v-else-if="clearable && hasSelection && !disabled"
        class="icon is-right is-clickable"
        role="button"
        tabindex="0"
        :aria-label="clearButtonAriaLabel"
        @click="clear"
        @keydown.enter="clear"
        @keydown.space.prevent="clear"
      >
        <i class="fas fa-times has-text-grey" aria-hidden="true"></i>
      </span>
      <span
        v-else-if="showOpenButton && !disabled"
        class="icon is-right is-clickable is-hidden-touch"
        role="button"
        tabindex="0"
        :aria-label="openButtonAriaLabel"
        @click="openDropdown"
        @keydown.enter="openDropdown"
        @keydown.space.prevent="openDropdown"
      >
        <i class="fas fa-chevron-down has-text-grey-light" aria-hidden="true"></i>
      </span>
    </div>

    <div v-else class="dropdown-trigger">
      <button
        ref="buttonRef"
        type="button"
        class="button is-small"
        :class="buttonClass"
        :disabled="disabled"
        :title="buttonTitle"
        aria-haspopup="listbox"
        :aria-expanded="isOpen.toString()"
        :aria-controls="listboxId"
        @click="toggle"
        @keydown="handleKeydown"
      >
        <slot
          name="button-label"
          :selected-option="selectedOption"
          :display-value="selectedTriggerLabel"
        >
          <span>{{ selectedTriggerLabel || buttonPlaceholder }}</span>
        </slot>
        <span class="icon is-small">
          <i :class="buttonIconClass" aria-hidden="true"></i>
        </span>
      </button>
    </div>

    <div
      v-if="isInputTrigger"
      v-show="showDropdown"
      :id="listboxId"
      ref="listRef"
      class="combobox-dropdown"
      role="listbox"
      :aria-label="ariaLabel"
      @scroll.passive="onListScroll"
    >
      <template v-if="isOpen && groupedOptions.length > 0">
        <template v-for="(group, groupIndex) in renderedGroupedOptions" :key="group.key">
          <div v-if="group.label" class="dropdown-group-label">{{ group.label }}</div>
          <div
            v-for="(option, optionIndex) in group.options"
            :id="getOptionId(groupIndex, optionIndex)"
            :key="getOptionKey(option, groupIndex, optionIndex)"
            :ref="(el) => setItemRef(el, getFlatIndex(groupIndex, optionIndex))"
            class="dropdown-item"
            :class="{
              'is-active': isActiveOption(groupIndex, optionIndex),
              'is-disabled': isDisabledOption(option),
            }"
            role="option"
            :aria-selected="isSelectedOption(option)"
            :aria-disabled="isDisabledOption(option)"
            @pointerdown.prevent="selectOption(option)"
            @mouseenter="
              !isDisabledOption(option) && setActiveIndex(getFlatIndex(groupIndex, optionIndex))
            "
          >
            <slot
              name="option"
              :option="option"
              :selected="isSelectedOption(option)"
              :disabled="isDisabledOption(option)"
              :label="getOptionLabel(option)"
              :value="getOptionValue(option)"
            >
              <span>{{ getOptionLabel(option) }}</span>
              <span v-if="isSelectedOption(option)" class="icon is-small has-text-success ml-2">
                <i class="fas fa-check"></i>
              </span>
            </slot>
          </div>
        </template>
        <slot
          name="list-footer"
          :hidden-count="hiddenOptionCount"
          :can-scroll-for-more="canIncreaseRenderLimitByScroll"
        />
      </template>
      <div v-else-if="loading" class="combobox-no-results has-text-grey">{{ loadingText }}</div>
      <div v-else-if="showNoResults" class="combobox-no-results has-text-grey">
        <slot name="no-results">{{ noResultsText }}</slot>
      </div>
    </div>

    <div v-else :id="listboxId" class="dropdown-menu" role="listbox" :aria-label="ariaLabel">
      <div
        v-show="showDropdown"
        ref="listRef"
        class="dropdown-content combobox-dropdown"
        :class="{ 'combobox-dropdown--embedded': embeddedMenu }"
        @scroll.passive="onListScroll"
      >
        <template v-if="isOpen && groupedOptions.length > 0">
          <template v-for="(group, groupIndex) in renderedGroupedOptions" :key="group.key">
            <div v-if="group.label" class="dropdown-group-label">{{ group.label }}</div>
            <div
              v-for="(option, optionIndex) in group.options"
              :id="getOptionId(groupIndex, optionIndex)"
              :key="getOptionKey(option, groupIndex, optionIndex)"
              :ref="(el) => setItemRef(el, getFlatIndex(groupIndex, optionIndex))"
              class="dropdown-item"
              :class="{
                'is-active': isActiveOption(groupIndex, optionIndex),
                'is-disabled': isDisabledOption(option),
              }"
              role="option"
              :aria-selected="isSelectedOption(option)"
              :aria-disabled="isDisabledOption(option)"
              @pointerdown.prevent="selectOption(option)"
              @mouseenter="
                !isDisabledOption(option) && setActiveIndex(getFlatIndex(groupIndex, optionIndex))
              "
            >
              <slot
                name="option"
                :option="option"
                :selected="isSelectedOption(option)"
                :disabled="isDisabledOption(option)"
                :label="getOptionLabel(option)"
                :value="getOptionValue(option)"
              >
                <span>{{ getOptionLabel(option) }}</span>
                <span v-if="isSelectedOption(option)" class="icon is-small has-text-success ml-2">
                  <i class="fas fa-check"></i>
                </span>
              </slot>
            </div>
          </template>
          <slot
            name="list-footer"
            :hidden-count="hiddenOptionCount"
            :can-scroll-for-more="canIncreaseRenderLimitByScroll"
          />
        </template>
        <div v-else-if="loading" class="combobox-no-results has-text-grey">{{ loadingText }}</div>
        <div v-else-if="showNoResults" class="combobox-no-results has-text-grey">
          <slot name="no-results">{{ noResultsText }}</slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, watch } from "vue";
  import Fuse from "fuse.js";

  import { useCombobox } from "../lib/use-combobox";
  import { useComboboxBlur } from "../lib/use-combobox-blur";

  function generateComboboxId(prefix) {
    return `${prefix}-${Math.random().toString(36).slice(2, 11)}`;
  }

  /**
   * Shared combobox/listbox primitive.
   *
   * Usage examples:
   *
   * Searchable input with local filtering (nutrient-combobox.vue):
   * ```vue
   * <BaseCombobox
   *   :model-value="modelValue"
   *   :items="items"
   *   value-key="value"
   *   label-key="label"
   *   group-key="group"
   *   trigger-mode="input"
   *   filter-mode="internal"
   *   fuzzy-search
   *   select-exact-match-on-blur
   *   @update:model-value="emit('update:modelValue', $event)"
   * />
   * ```
   *
   * Static button dropdown (unit-dropdown.vue):
   * ```vue
   * <BaseCombobox
   *   :model-value="modelValue"
   *   :items="items"
   *   value-key="value"
   *   label-key="label"
   *   trigger-label-key="value"
   *   trigger-mode="button"
   *   :button-placeholder="'Select...'"
   *   @update:model-value="emit('update:modelValue', $event)"
   * />
   * ```
   *
   * Async input with external filtering (order-item-inventory-combobox.vue):
   * ```vue
   * <BaseCombobox
   *   :model-value="modelValue"
   *   :items="serverResults"
   *   :search-term="query"
   *   :loading="loading"
   *   value-key="value"
   *   label-key="label"
   *   trigger-mode="input"
   *   filter-mode="external"
   *   @update:search-term="query = $event"
   *   @select="handleSelect"
   * />
   * ```
   */
  const props = defineProps({
    // Core value + option mapping.
    // `items` should be an array of objects. `valueKey` identifies the emitted
    // model value; `labelKey` identifies the default human-readable label.
    modelValue: { type: [String, Number, Boolean, Object, Array], default: "" },
    items: { type: Array, default: () => [] },
    labelKey: { type: String, default: "label" },
    valueKey: { type: String, default: "value" },

    // Optional alternate label for the closed trigger.
    // Useful for button mode when the option row is richer than the trigger.
    triggerLabelKey: { type: String, default: "" },

    // Option organization + search.
    // `groupKey` adds section headers; `searchKeys` controls which fields are
    // searched in input mode. When omitted, search falls back to `labelKey`.
    groupKey: { type: String, default: "" },
    searchKeys: { type: Array, default: () => [] },

    // Shared presentation/state.
    // `placeholder` is used by input mode; button mode uses `buttonPlaceholder`.
    placeholder: { type: String, default: "Search..." },
    disabled: { type: Boolean, default: false },
    excludeValues: { type: Array, default: () => [] },
    disabledValues: { type: Array, default: () => [] },
    ariaLabel: { type: String, default: "Options" },
    hasError: { type: Boolean, default: false },
    noResultsText: { type: String, default: "No matching options found" },

    // Input-mode search source.
    // - `internal`: the base component filters `items` locally
    // - `external`: the parent controls `searchTerm`, result fetching, and `items`
    filterMode: {
      type: String,
      default: "internal",
      validator(value) {
        return ["internal", "external"].includes(value);
      },
    },
    searchTerm: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    loadingText: { type: String, default: "Loading..." },

    // Show the no-results message even with an empty search term. External searches
    // that run on focus (e.g. open lot codes) can legitimately return nothing, and
    // the empty box should say so rather than silently close.
    showNoResultsWhenEmpty: { type: Boolean, default: false },

    // Rendering window. Options beyond this count stay out of the DOM until the user
    // scrolls or arrows past the end of the window; Infinity renders everything.
    maxRenderedItems: { type: Number, default: Infinity },

    // Ceiling for scroll-driven growth of the window. Once this many options are
    // rendered, scrolling stops loading more and the footer asks the user to refine
    // their search instead. Infinity keeps loading on every scroll to the bottom.
    maxScrollRenderedItems: { type: Number, default: Infinity },

    // Input-mode clear behavior.
    // `clearValue` is emitted through `update:modelValue` before `clear()`.
    clearable: { type: Boolean, default: true },
    clearValue: { type: [String, Number, Boolean, Object, Array], default: "" },
    clearButtonAriaLabel: { type: String, default: "Clear selection" },
    openButtonAriaLabel: { type: String, default: "Open dropdown" },
    showOpenButton: { type: Boolean, default: true },

    // Input-mode blur + search behavior.
    // These are typically used by searchable comboboxes, not static dropdowns.
    selectExactMatchOnBlur: { type: Boolean, default: false },
    clearInvalidInputOnBlur: { type: Boolean, default: false },
    fuzzySearch: { type: Boolean, default: false },

    // Shared DOM/class customization.
    idPrefix: { type: String, default: "combobox" },
    inputClass: { type: [String, Object, Array], default: "" },

    // Trigger selection.
    // - "input": searchable text input with optional clear button
    // - "button": static button + dropdown listbox
    triggerMode: {
      type: String,
      default: "input",
      validator(value) {
        return ["input", "button"].includes(value);
      },
    },

    // Button-mode presentation.
    // These props are ignored in input mode.
    buttonPlaceholder: { type: String, default: "Select..." },
    buttonTitle: { type: String, default: "" },
    buttonClass: { type: [String, Object, Array], default: "" },
    buttonIconClass: { type: String, default: "fas fa-angle-down" },

    // Button-mode layout variant.
    // `embeddedMenu` removes the floating-menu chrome so the list can live
    // inside an existing dropdown shell, as in `unit-dropdown`.
    embeddedMenu: { type: Boolean, default: false },
  });

  const emit = defineEmits([
    "update:modelValue",
    "update:searchTerm",
    "select",
    "clear",
    "invalid-input",
    "focus",
    "blur",
  ]);

  const internalSearchTerm = ref("");
  const inputRef = ref(null);
  const buttonRef = ref(null);
  const listboxId = `${generateComboboxId(props.idPrefix)}-listbox`;

  const isInputTrigger = computed(() => props.triggerMode === "input");
  const isButtonTrigger = computed(() => props.triggerMode === "button");
  const isInternalFilterMode = computed(() => props.filterMode === "internal");
  const isExternalFilterMode = computed(() => props.filterMode === "external");

  const currentSearchTerm = computed({
    get() {
      return isExternalFilterMode.value ? props.searchTerm : internalSearchTerm.value;
    },
    set(value) {
      if (isExternalFilterMode.value) {
        emit("update:searchTerm", value);
      } else {
        internalSearchTerm.value = value;
      }
    },
  });

  function hasValue(value = props.modelValue) {
    return value !== "" && value !== null && value !== undefined;
  }

  function normalizeText(value) {
    return String(value ?? "")
      .trim()
      .toLowerCase();
  }

  function getOptionLabel(option) {
    return option?.[props.labelKey] ?? "";
  }

  function getOptionValue(option) {
    return option?.[props.valueKey];
  }

  function getTriggerLabel(option) {
    return option?.[props.triggerLabelKey || props.labelKey] ?? "";
  }

  function isSelectedOption(option) {
    return getOptionValue(option) === props.modelValue;
  }

  function isDisabledOption(option) {
    return props.disabledValues.includes(getOptionValue(option));
  }

  function getOptionKey(option, groupIndex, optionIndex) {
    return `${groupIndex}-${optionIndex}-${String(getOptionValue(option) ?? "")}`;
  }

  const hasSelection = computed(() => hasValue());

  const selectedOption = computed(
    () => props.items.find((item) => getOptionValue(item) === props.modelValue) || null
  );

  const selectedTriggerLabel = computed(() =>
    selectedOption.value ? getTriggerLabel(selectedOption.value) : ""
  );

  const availableItems = computed(() =>
    props.items.filter((item) => !props.excludeValues.includes(getOptionValue(item)))
  );

  const resolvedSearchKeys = computed(() =>
    props.searchKeys.length > 0 ? props.searchKeys : [props.labelKey]
  );

  const fuse = computed(() => {
    if (!props.fuzzySearch) return null;

    return new Fuse(availableItems.value, {
      keys: resolvedSearchKeys.value,
      threshold: 0.4,
      ignoreLocation: true,
    });
  });

  const filteredItems = computed(() => {
    if (isButtonTrigger.value) return availableItems.value;
    if (isExternalFilterMode.value) return availableItems.value;

    const query = currentSearchTerm.value.trim();
    if (!query) return availableItems.value;

    if (fuse.value) {
      return fuse.value.search(query).map((result) => result.item);
    }

    const normalizedQuery = normalizeText(query);
    return availableItems.value.filter((item) =>
      resolvedSearchKeys.value.some((key) => normalizeText(item?.[key]).includes(normalizedQuery))
    );
  });

  const groupedOptions = computed(() => {
    const groups = new Map();

    for (const item of filteredItems.value) {
      const groupLabel = props.groupKey ? item?.[props.groupKey] || "" : "";
      if (!groups.has(groupLabel)) {
        groups.set(groupLabel, []);
      }
      groups.get(groupLabel).push(item);
    }

    return Array.from(groups.entries()).map(([label, options]) => ({
      key: label || "__ungrouped__",
      label,
      options,
    }));
  });

  const showNoResults = computed(
    () =>
      !props.loading &&
      groupedOptions.value.length === 0 &&
      (props.showNoResultsWhenEmpty || currentSearchTerm.value.trim().length > 0)
  );
  const showDropdown = computed(
    () =>
      isOpen.value &&
      !props.disabled &&
      (props.loading || groupedOptions.value.length > 0 || showNoResults.value)
  );

  const flatOptions = computed(() => {
    const result = [];
    groupedOptions.value.forEach((group, groupIndex) => {
      group.options.forEach((option, optionIndex) => {
        result.push({ groupIndex, optionIndex, option });
      });
    });
    return result;
  });

  const {
    isOpen,
    activeIndex,
    containerRef,
    listRef,
    setItemRef,
    setActiveIndex,
    handleKeydown,
    open,
    close,
    toggle,
  } = useCombobox({
    items: flatOptions,
    onSelect: (flatItem) => selectOption(flatItem.option),
    isItemDisabled: (flatItem) => isDisabledOption(flatItem.option),
  });

  const { handleBlur: handleComboboxBlur } = useComboboxBlur({ containerRef, close });

  const renderLimit = ref(props.maxRenderedItems);

  const renderedGroupedOptions = computed(() => {
    let remaining = renderLimit.value;
    if (remaining >= filteredItems.value.length) return groupedOptions.value;

    const groups = [];
    for (const group of groupedOptions.value) {
      if (remaining <= 0) break;
      const options = group.options.slice(0, remaining);
      remaining -= options.length;
      groups.push(options.length === group.options.length ? group : { ...group, options });
    }
    return groups;
  });

  const hiddenOptionCount = computed(() =>
    Math.max(0, filteredItems.value.length - renderLimit.value)
  );

  const canIncreaseRenderLimitByScroll = computed(
    () => renderLimit.value < props.maxScrollRenderedItems
  );

  function onListScroll(event) {
    if (!canIncreaseRenderLimitByScroll.value) return;
    const list = event.target;
    if (list.scrollHeight - list.scrollTop - list.clientHeight > 40) return;
    if (renderLimit.value < filteredItems.value.length) {
      renderLimit.value = Math.min(
        renderLimit.value + props.maxRenderedItems,
        props.maxScrollRenderedItems
      );
    }
  }

  // Keyboard navigation is not bound by the window: arrowing past its end (or wrapping
  // to the last option) extends the rendered list to keep the active option visible.
  watch(activeIndex, (index) => {
    if (index >= renderLimit.value) renderLimit.value = index + props.maxRenderedItems;
  });

  watch([currentSearchTerm, isOpen], () => {
    renderLimit.value = props.maxRenderedItems;
  });

  const activeDescendantId = computed(() => {
    if (activeIndex.value >= 0 && flatOptions.value[activeIndex.value]) {
      const { groupIndex, optionIndex } = flatOptions.value[activeIndex.value];
      return getOptionId(groupIndex, optionIndex);
    }

    return undefined;
  });

  function getFlatIndex(groupIndex, optionIndex) {
    let index = 0;
    for (let currentGroupIndex = 0; currentGroupIndex < groupIndex; currentGroupIndex += 1) {
      index += groupedOptions.value[currentGroupIndex]?.options.length || 0;
    }
    return index + optionIndex;
  }

  function getOptionId(groupIndex, optionIndex) {
    return `${listboxId}-option-${groupIndex}-${optionIndex}`;
  }

  function isActiveOption(groupIndex, optionIndex) {
    return activeIndex.value === getFlatIndex(groupIndex, optionIndex);
  }

  function onInput(event) {
    currentSearchTerm.value = event.target.value;
    if (!isOpen.value) open();
  }

  function onFocus() {
    open();
    emit("focus");
  }

  function onBlur() {
    if (props.selectExactMatchOnBlur && currentSearchTerm.value && !hasSelection.value) {
      const normalizedQuery = normalizeText(currentSearchTerm.value);
      const exactMatch = availableItems.value.find(
        (item) => normalizeText(getOptionLabel(item)) === normalizedQuery
      );

      if (exactMatch) {
        selectOption(exactMatch);
      } else if (props.clearInvalidInputOnBlur && currentSearchTerm.value.trim()) {
        emit("invalid-input", currentSearchTerm.value.trim());
        currentSearchTerm.value = "";
      }
    }

    handleComboboxBlur();
    emit("blur");
  }

  function selectOption(option) {
    if (isDisabledOption(option)) return;

    const newValue = getOptionValue(option);
    const didChange = newValue !== props.modelValue;

    if (didChange) {
      emit("update:modelValue", newValue);
      emit("select", option);
    }

    if (isInputTrigger.value && isInternalFilterMode.value) {
      currentSearchTerm.value = getTriggerLabel(option);
    }

    close();
    if (isButtonTrigger.value) {
      buttonRef.value?.focus();
    }
  }

  function clear() {
    if (hasSelection.value) {
      emit("update:modelValue", props.clearValue);
    }

    emit("clear");
    currentSearchTerm.value = "";
    inputRef.value?.focus();
  }

  function openDropdown() {
    currentSearchTerm.value = "";
    open();
    inputRef.value?.focus();
  }

  function focusTrigger() {
    if (isInputTrigger.value) {
      inputRef.value?.focus();
    } else {
      buttonRef.value?.focus();
    }
  }

  defineExpose({ focusTrigger });

  watch(
    () => [props.modelValue, props.items],
    ([newValue]) => {
      if (isInputTrigger.value && isInternalFilterMode.value && hasValue(newValue)) {
        const selected = props.items.find((item) => getOptionValue(item) === newValue);
        internalSearchTerm.value = selected ? getTriggerLabel(selected) : "";
      } else if (isInputTrigger.value && isInternalFilterMode.value) {
        internalSearchTerm.value = "";
      }
    },
    { immediate: true, deep: true }
  );
</script>

<style lang="scss" scoped>
  @import "~/label/css/labels/supplements/combobox";

  .base-combobox {
    @include combobox-container;
  }

  .combobox-dropdown {
    @include combobox-dropdown(180px);
    border-radius: $radius;
    margin-top: 2px;
  }

  .combobox-dropdown--embedded {
    position: static;
    border: none;
    box-shadow: none;
    margin-top: 0;
  }

  .dropdown-group-label {
    @include combobox-group-label;
  }

  .combobox-no-results {
    padding: 0.375rem 1rem;
    font-size: 0.875em;
    cursor: default;
  }

  .dropdown-item {
    @include combobox-item;
    @include combobox-selected-indicator;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &.is-disabled {
      cursor: default;
      opacity: 0.5;
    }

    &:hover,
    &.is-active {
      :deep(.has-text-grey),
      :deep(.has-text-grey-light),
      :deep(.has-text-success) {
        color: $white !important;
      }
    }
  }
</style>
